import React from 'react'
import Bugsnag, { Event, OnErrorCallback } from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import APP_VERSION from 'app-version'

import { sleep } from 'utilities/promises'
import {
  dropQueryValuesInText,
  templateSecretsDeep,
  templateSecretsInText,
} from 'utilities/secret-urls'
import { redactPersonalText, redactReportData } from 'utilities/redact-report'

// Key names whose values are credentials, replaced with "[REDACTED]" at every
// depth of metadata and breadcrumb metadata when a report is sent. Keep in
// step with BUGSNAG_FILTERS in node/utilities/error/redact-secrets.ts. Setting
// this replaces Bugsnag's default of ['password'].
export const REDACTED_KEYS: RegExp[] = [
  /password/i,
  // Ends in "token" so counts such as promptTokens survive.
  /token$/i,
  /secret/i,
  /^authorization$/i,
  /cookie/i,
  /api[-_]?key/i,
  /^meetingCode$/i,
  /^(kickoffEventRef|nylasBookingRef|bookingRef)$/i,
]

// The page address rides on every report: in its context, its request URL,
// navigation and request breadcrumbs, and often the error message. On a /meet
// page that address is the join link, so each URL is templated before the
// report leaves (utilities/secret-urls has the list of routes).
export const templateSecretsInEvent = (event: Event) => {
  if (event.context) event.context = templateSecretsInText(event.context)
  if (event.request?.url) {
    event.request.url = templateSecretsInText(event.request.url)
  }
  for (const error of event.errors) {
    error.errorMessage = templateSecretsInText(error.errorMessage)
  }
  for (const breadcrumb of event.breadcrumbs) {
    breadcrumb.message = templateSecretsInText(breadcrumb.message)
    breadcrumb.metadata = templateSecretsDeep(
      breadcrumb.metadata
    ) as typeof breadcrumb.metadata
  }
  // Event has no public way to list its metadata sections.
  const metadata = (event as unknown as { _metadata?: unknown })._metadata
  if (metadata && typeof metadata === 'object') {
    for (const [section, values] of Object.entries(metadata)) {
      if (!values || typeof values !== 'object') continue
      const templated = templateSecretsDeep(values)
      // An Error or other class instance comes back as itself; re-adding it
      // would keep only its enumerable fields.
      if (templated === values) continue
      event.clearMetadata(section)
      event.addMetadata(section, templated as Record<string, unknown>)
    }
  }
}

// Report text: emails and phone numbers out, and every URL's query values
// replaced by their keys (a query can hold an email, a name or a token).
const redactReportText = (text: string) =>
  dropQueryValuesInText(redactPersonalText(text))

// Kickoff has no BAA with BugSnag, so a report must carry ids, not personal
// or health details. Call sites send ids; this is the safety net, with the key
// rules in utilities/redact-report. It runs after templateSecretsInEvent, so
// it also sees what a report's own callback added.
const redactEvent = (event: Event) => {
  const { id } = event.getUser()
  event.setUser(id)
  if (event.context) event.context = redactReportText(event.context)
  if (event.request?.url) {
    event.request.url = redactReportText(event.request.url)
  }
  for (const error of event.errors) {
    error.errorMessage = redactReportText(error.errorMessage)
  }
  for (const breadcrumb of event.breadcrumbs) {
    breadcrumb.message = redactReportText(breadcrumb.message)
    breadcrumb.metadata = redactReportData(
      breadcrumb.metadata,
      redactReportText
    ) as typeof breadcrumb.metadata
  }
  const metadata = (event as unknown as { _metadata?: unknown })._metadata
  if (metadata && typeof metadata === 'object') {
    for (const [section, values] of Object.entries(metadata)) {
      if (!values || typeof values !== 'object') continue
      const redacted = redactReportData(values, redactReportText)
      event.clearMetadata(section)
      event.addMetadata(section, redacted as Record<string, unknown>)
    }
  }
}

// Bugsnag logs a throwing callback and sends the report anyway, so a failure
// here must leave nothing behind: the report goes out with its metadata,
// breadcrumbs, messages, context and address dropped, or not at all.
export const redactPersonalInEvent = (event: Event) => {
  try {
    redactEvent(event)
  } catch {
    try {
      const metadata = (event as unknown as { _metadata?: object })._metadata
      for (const section of Object.keys(metadata ?? {})) {
        event.clearMetadata(section)
      }
      event.breadcrumbs = []
      for (const error of event.errors) {
        error.errorMessage = 'Report redaction failed'
      }
      event.context = undefined
      if (event.request) event.request.url = undefined
      event.setUser(undefined)
      event.addMetadata('redaction', { failed: true })
    } catch {
      return false
    }
  }
  return undefined
}

const bugsnagClient = Bugsnag.start({
  apiKey:
    typeof window !== 'undefined'
      ? process.env.BUGSNAG_BROWSER_API_KEY
      : process.env.BUGSNAG_SSR_API_KEY,
  releaseStage: process.env.APP_ENV,
  enabledReleaseStages: ['staging', 'production', 'growth'],
  plugins: [new BugsnagPluginReact(React)],
  appVersion: APP_VERSION,
  redactedKeys: REDACTED_KEYS,
  // No visitor IP: BugSnag would store it, and use it as the user id of a
  // signed-out visitor.
  collectUserIp: false,
  // No inline-script source: an error in one sends lines of the page's HTML,
  // server-rendered names and __NEXT_DATA__ included.
  trackInlineScripts: false,
  // No click ('user') breadcrumbs: they record the text of what was clicked,
  // such as a client's name and a message preview. No console ('log')
  // breadcrumbs: they record whatever was logged, whole records included.
  enabledBreadcrumbTypes: [
    'navigation',
    'request',
    'process',
    'state',
    'error',
    'manual',
  ],
})

// Templating and redaction have to run after every other callback, not as the client's
// `onError`: Bugsnag runs a report's own callback last (`notify(error, cb)`,
// the React error boundary, the unhandled-rejection handler), and those set
// context and add metadata too. Every report goes through `_notify`, which
// concatenates the client's callbacks with the report's, so an array runs each
// in turn with Bugsnag's usual sync and async handling.
const notifyWithCallbacks = bugsnagClient._notify.bind(bugsnagClient)
bugsnagClient._notify = (event, onError, postReportCallback) =>
  notifyWithCallbacks(
    event,
    ([] as OnErrorCallback[]).concat(
      onError ?? [],
      templateSecretsInEvent,
      redactPersonalInEvent
    ) as unknown as OnErrorCallback,
    postReportCallback
  )

export const bugsnagNotifyAsync = async error => {
  await Promise.race([
    sleep(2000), // timeout
    new Promise(resolve => {
      Bugsnag.notify(error, () => {
        console.log('Bugsnag notified!')
        resolve(true)
      })
    }),
  ])
}

export default bugsnagClient
